import { useState } from "react";
import { Stack, Typography, Box, Link, Alert } from "@mui/material";
import MarkEmailReadOutlinedIcon from "@mui/icons-material/MarkEmailReadOutlined";

const RESEND_COOLDOWN_SECONDS = 30;

// Step 3 of onboarding: not a separate route/page, just a confirmation
// message rendered in place of the form once "Confirm Your Email" succeeds.
// "Resend" re-triggers the same sendVerificationEmail call (passed down from
// OnboardingIntro) in case the first email didn't arrive. "Edit email" sends
// the user back to step 1 to fix the address.
export default function VerifyEmailStep({ email, onResend, onEditEmail }) {
  const [isResending, setIsResending] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [resendError, setResendError] = useState("");

  const startCooldown = () => {
    setCooldown(RESEND_COOLDOWN_SECONDS);
    const timer = setInterval(() => {
      setCooldown((c) => {
        if (c <= 1) {
          clearInterval(timer);
          return 0;
        }
        return c - 1;
      });
    }, 1000);
  };

  const handleResend = async () => {
    if (isResending || cooldown > 0 || !email) return;
    setResendError("");
    setIsResending(true);
    try {
      await onResend(email);
      startCooldown();
    } catch (err) {
      setResendError("Couldn't resend the email. Please try again.");
    } finally {
      setIsResending(false);
    }
  };

  return (
    <Stack spacing={3} alignItems="center" textAlign="center">
      <Box
        sx={{
          width: 64,
          height: 64,
          borderRadius: "50%",
          bgcolor: "primary.lighter",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <MarkEmailReadOutlinedIcon sx={{ fontSize: 32, color: "primary.main" }} />
      </Box>

      <Box>
        <Typography variant="h6" fontWeight={600}>
          Almost There!
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Check your inbox for an email confirmation link to continue your
          account setup.
        </Typography>
      </Box>

      <Box>
        <Typography variant="body2" color="text.secondary">
          We sent a verification link to:
        </Typography>
        <Typography variant="body1" fontWeight={700}>
          {email}
        </Typography>
      </Box>

      <Typography variant="body2">
        <Link component="button" type="button" underline="hover" onClick={onEditEmail}>
          Wrong email?
        </Link>
        {"  "}
        <Link
          component="button"
          type="button"
          underline="hover"
          disabled={isResending || cooldown > 0 || !email}
          onClick={handleResend}
        >
          {cooldown > 0
            ? `Resend in ${cooldown}s`
            : isResending
            ? "Resending…"
            : "Resend"}
        </Link>
      </Typography>

      {resendError && <Alert severity="error" sx={{ width: "100%" }}>{resendError}</Alert>}

      <Alert severity="warning" sx={{ width: "100%" }}>
        Can&apos;t find it? Please check your spam or junk folder. Verification
        emails sometimes land there.
      </Alert>
    </Stack>
  );
}